import { AttributeInput } from "@dashboard/components/Attributes";
import { ProductErrorCode, ProductErrorWithAttributesFragment } from "@dashboard/graphql";
import { IntlShape } from "react-intl";
import { z } from "zod";

import { ProductCreateData } from "../components/ProductCreatePage";
import { ProductVariantCreateData } from "../components/ProductVariantCreatePage/form";
import { ProductVariantUpdateSubmitData } from "../components/ProductVariantPage/form";

export const validatePrice = (price: string) => price === "" || parseInt(price, 10) < 0;

export const validateCostPrice = (price: string) => price !== "" && parseInt(price, 10) < 0;

const toChannelPriceField = (id: string) => `${id}-channelListing-price`;
const createRequiredError = (
  field: string,
  message: string | null = null,
): ProductErrorWithAttributesFragment => ({
  __typename: "ProductError",
  code: ProductErrorCode.REQUIRED,
  field,
  message,
  attributes: [],
});

// Mirrors the filter applied to attributes before they are sent to the API in
// ProductVariantCreate. Both call sites have to agree on what "empty" means,
// otherwise the form can pass validation and still be rejected by the server.
export const isAttributeValueFilled = (attribute: AttributeInput) =>
  !!attribute.value?.length && attribute.value[0] !== "";

const createRequiredAttributeError = (attributeId: string): ProductErrorWithAttributesFragment => ({
  __typename: "ProductError",
  code: ProductErrorCode.REQUIRED,
  field: "attributes",
  // Left null on purpose - getProductErrorMessage renders REQUIRED from the code.
  message: null,
  // AttributeListItem matches errors to rows through this list.
  attributes: [attributeId],
});

/**
 * Flags required variant attributes that were left blank.
 *
 * This cannot be delegated to the API: when no attribute is supplied at all,
 * productVariantCreate raises REQUIRED without any attribute ids attached, so
 * there is nothing to map back onto a field and every row stays unflagged.
 *
 * Creation only. productVariantUpdate cleans attributes with creation=False and
 * deliberately skips the required check, so running this on update would block
 * editing variants whose product type gained a required attribute afterwards.
 */
export const validateVariantAttributes = (
  attributes: AttributeInput[] | undefined,
): ProductErrorWithAttributesFragment[] =>
  (attributes ?? [])
    .filter(attribute => attribute.data.isRequired && !isAttributeValueFilled(attribute))
    .map(attribute => createRequiredAttributeError(attribute.id));

export const validateProductCreateData = (data?: ProductCreateData) => {
  let errors: ProductErrorWithAttributesFragment[] = [];

  if (!data) {
    return errors;
  }

  if (!data.productType) {
    errors = [...errors, createRequiredError("productType")];
  }

  if (!data.name) {
    errors = [...errors, createRequiredError("name")];
  }

  const { productType, channelListings } = data;

  // Without a product type there is nothing to say about prices yet: whether a
  // channel price is required depends on hasVariants. Reading it off an
  // unselected product type threw, which lost the whole submit. WSM6-3027.
  if (productType && !productType.hasVariants && channelListings) {
    const emptyPrices = data.channelListings
      .filter(channel => channel.price?.length === 0)
      .map(({ id }) => createRequiredError(toChannelPriceField(id)));

    errors = [...errors, ...emptyPrices];
  }

  return errors;
};

const channelListingValueSchema = z.object({
  price: z.number().or(z.string().min(1)),
});

const channelListingSchema = z
  .object({
    channelListings: z.array(
      z.object({
        value: channelListingValueSchema,
      }),
    ),
    variantName: z.string().min(1),
  })
  .partial();

export type ProductVariantType = ProductVariantCreateData | ProductVariantUpdateSubmitData;

const handleValidationError = (
  error: z.ZodIssue,
  data: ProductVariantType,
  defaultMessage: string,
) => {
  const defaultError = createRequiredError(error.path.join("-"), defaultMessage);

  switch (error.code) {
    case "too_small":
    case "invalid_union":
    case "invalid_type":
      if (error.path.includes("price") && error.path.includes("channelListings")) {
        // Due to the way the form was written
        // The path is in format "channelListing => {index} => price"
        const index = error.path[1] as number;
        const listing = data.channelListings[index];

        return createRequiredError(toChannelPriceField(listing.id), defaultMessage);
      }

      return defaultError;
    default:
      return defaultError;
  }
};

export const validateProductVariant = (data: ProductVariantType, intl: IntlShape) => {
  const result = channelListingSchema.safeParse(data);

  const defaultMessage = intl.formatMessage({
    defaultMessage: "This field cannot be blank",
    id: "8pVWve",
  });

  return result.success === true
    ? []
    : result.error.issues.map(error => handleValidationError(error, data, defaultMessage));
};
